import { useEffect, useState, useCallback, useRef } from "react";
import "@/App.css";
import { Toaster, toast } from "sonner";
import { motion } from "framer-motion";
import {
  Trophy, Share2, Copy, MessageCircle, CalendarX2, CalendarDays, BarChart3, Search, Bell, CheckCircle2, Download, LogOut, Share, Plus, X, Vote, ShieldCheck,
} from "lucide-react";
import { VotesBoard } from "@/components/VotesBoard";
import { AwardsCeremony } from "@/components/AwardsCeremony";
import { RatingsDialog } from "@/components/RatingsDialog";
import { RatingsBoard } from "@/components/RatingsBoard";
import { AdminPanel } from "@/components/AdminPanel";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { pushSupported, notificationStatus, enablePush, ensurePushSubscribed, sendTestPush } from "@/lib/push";
import { buildWhatsappText, openWhatsapp, buildReminderText, openWhatsappReminder, openWhatsappLeaderboard, openWhatsappPromotion, missingConfirmations, openWhatsappConfirmReminder, confirmedAndWaitlist } from "@/lib/padel";
import { MatchCard } from "@/components/MatchCard";
import { CreateMatchDialog } from "@/components/CreateMatchDialog";
import { EditMatchDialog } from "@/components/EditMatchDialog";
import { ResultDialog } from "@/components/ResultDialog";
import { CommentsDialog } from "@/components/CommentsDialog";
import { Leaderboard } from "@/components/Leaderboard";
import { PairsBoard } from "@/components/PairsBoard";
import { MonthCalendar, matchesOnDate, todayString } from "@/components/MonthCalendar";
import { PlayerHistoryDialog } from "@/components/PlayerHistoryDialog";
import { Avatar } from "@/components/Avatar";
import { AuthScreen } from "@/components/AuthScreen";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const FILTERS = [
  { id: "tutte", label: "Tutte" },
  { id: "disponibili", label: "Disponibili" },
  { id: "complete", label: "Complete" },
  { id: "mie", label: "Mie partite" },
];

const isIosDevice = typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent) && !/windows phone/i.test(navigator.userAgent);
const isStandalonePwa = typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator?.standalone === true);

function App() {
  const { user, loading: authLoading, logout, updateUser } = useAuth();
  const playerName = user?.username || "";
  const [matches, setMatches] = useState([]);
  const [resultSearch, setResultSearch] = useState("");
  const [photos, setPhotos] = useState({});
  const [board, setBoard] = useState([]);
  const [mvp, setMvp] = useState(null);
  const [pastMvps, setPastMvps] = useState([]);
  const [pairs, setPairs] = useState([]);
  const [boardView, setBoardView] = useState("giocatori");
  const [view, setView] = useState(() => {
    const v = new URLSearchParams(window.location.search).get("view");
    return ["votazioni", "admin", "risultati"].includes(v) ? v : "partite";
  });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("tutte");
  const [search, setSearch] = useState("");
  const [partiteView, setPartiteView] = useState("lista");
  const [selectedDate, setSelectedDate] = useState(null);
  const [shareMatch, setShareMatch] = useState(null);
  const [resultMatch, setResultMatch] = useState(null);
  const [ratingsMatch, setRatingsMatch] = useState(null);
  const [ratingsRows, setRatingsRows] = useState([]);
  const [editMatch, setEditMatch] = useState(null);
  const [historyPlayer, setHistoryPlayer] = useState(null);
  const [commentsMatch, setCommentsMatch] = useState(null);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [pushOn, setPushOn] = useState(() => notificationStatus() === "granted");
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // One-time iOS install hint (iOS Safari never fires beforeinstallprompt).
  useEffect(() => {
    if (user && isIosDevice && !isStandalonePwa && !localStorage.getItem("padel_ios_hint_seen")) {
      setShowIosHint(true);
    }
  }, [user]);

  // Keep this device's push subscription linked to the current account.
  useEffect(() => {
    if (user) ensurePushSubscribed(user.username);
  }, [user]);

  const dismissIosHint = () => {
    localStorage.setItem("padel_ios_hint_seen", "1");
    setShowIosHint(false);
  };

  const handleInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      await installPrompt.userChoice;
      setInstallPrompt(null);
      return;
    }
    if (isIosDevice) { setShowIosHint(true); return; }
    toast("Per installare: menu del browser → 'Aggiungi a schermata Home'", { duration: 6000 });
  };

  const handleAddComment = async (matchId, name, text) => {
    try {
      const updated = await api.addComment(matchId, name, text);
      setCommentsMatch(updated);
      setMatches((prev) => prev.map((m) => (m.id === matchId ? updated : m)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const handleDeleteComment = async (matchId, commentId) => {
    try {
      const updated = await api.deleteComment(matchId, commentId);
      setCommentsMatch(updated);
      setMatches((prev) => prev.map((m) => (m.id === matchId ? updated : m)));
    } catch (e) {
      toast.error("Errore");
    }
  };

  const handleReactComment = async (matchId, commentId, emoji) => {
    try {
      const updated = await api.reactComment(matchId, commentId, playerName, emoji);
      setCommentsMatch(updated);
      setMatches((prev) => prev.map((m) => (m.id === matchId ? updated : m)));
    } catch (e) {
      toast.error("Errore");
    }
  };

  const handleReply = async (matchId, commentId, name, text) => {
    try {
      const updated = await api.addReply(matchId, commentId, name, text);
      setCommentsMatch(updated);
      setMatches((prev) => prev.map((m) => (m.id === matchId ? updated : m)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const handleDeleteReply = async (matchId, commentId, replyId) => {
    try {
      const updated = await api.deleteReply(matchId, commentId, replyId);
      setCommentsMatch(updated);
      setMatches((prev) => prev.map((m) => (m.id === matchId ? updated : m)));
    } catch (e) {
      toast.error("Errore");
    }
  };

  const handleEnablePush = async () => {
    if (!pushSupported()) { toast.error("Notifiche non supportate su questo dispositivo/browser"); return; }
    try {
      await enablePush(playerName);
      setPushOn(true);
      toast.success("Notifiche attivate! 🔔");
      // Give the push service a moment to register the new subscription before
      // sending the welcome notification, so it reliably reaches the device.
      setTimeout(() => { sendTestPush(playerName).catch(() => {}); }, 1200);
    } catch (e) {
      if (e.message === "denied") toast.error("Permesso notifiche negato. Abilitalo dalle impostazioni del browser.");
      else toast.error("Impossibile attivare le notifiche");
    }
  };

  const loadPlayers = useCallback(async () => {
    try {
      const players = await api.listPlayers();
      const map = {};
      players.forEach((p) => { map[p.key] = p.photo_path; });
      setPhotos(map);
    } catch (e) { console.error("loadPlayers failed", e); }
  }, []);

  const loadBoard = useCallback(async () => {
    try {
      setBoard(await api.leaderboard());
      const m = await api.mvp();
      setMvp(m.mvp);
      setPastMvps(await api.mvpHistory());
      setPairs(await api.pairs());
    } catch (e) { console.error("loadBoard failed", e); }
  }, []);

  const load = useCallback(async () => {
    try {
      const data = await api.listMatches();
      setMatches(data);
    } catch (e) {
      toast.error("Impossibile caricare le partite");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); loadPlayers(); loadBoard(); }, [load, loadPlayers, loadBoard]);

  const handleCreate = async (payload) => {
    try {
      await api.createMatch(payload);
      toast.success("Partita creata! 🎾");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nella creazione");
    }
  };

  const handleJoin = async (match) => {
    try {
      const updated = await api.joinMatch(match.id, playerName);
      const { confirmed } = confirmedAndWaitlist(updated);
      const inMain = confirmed.some((p) => p.name.toLowerCase() === playerName.toLowerCase());
      toast.success(inMain ? "Sei dentro! 🏆" : "Aggiunto alla lista d'attesa ⏳");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const handleLeave = async (match) => {
    const { confirmed, waitlist } = confirmedAndWaitlist(match);
    const wasConfirmed = confirmed.some((p) => p.name.toLowerCase() === playerName.toLowerCase());
    const promoted = wasConfirmed && waitlist.length > 0 ? waitlist[0].name : null;
    try {
      const updated = await api.leaveMatch(match.id, playerName);
      toast("Iscrizione annullata");
      if (promoted) {
        toast.success(`Si è liberato un posto! ${promoted} è stato promosso dalla lista d'attesa.`, {
          duration: 8000,
          action: {
            label: "Avvisa su WhatsApp",
            onClick: () => openWhatsappPromotion(updated, promoted),
          },
        });
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const handleDelete = async (match) => {
    try {
      await api.deleteMatch(match.id);
      toast.success("Partita eliminata");
      load();
      loadBoard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const handleEditMatch = async (matchId, payload) => {
    try {
      await api.editMatch(matchId, payload);
      toast.success("Partita aggiornata");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nella modifica");
      throw e;
    }
  };

  const handleKick = async (match, name) => {
    try {
      await api.kickPlayer(match.id, name);
      toast.success(`${name} rimosso dalla partita`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const handlePromote = async (match, name) => {
    try {
      await api.promotePlayer(match.id, name);
      toast.success(`${name} promosso tra i convocati! 🎾`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const handleOpenProfile = useCallback((name) => {
    const key = (name || "").toLowerCase();
    const row = board.find((r) => r.key === key);
    setHistoryPlayer(row || {
      key, name, points: 0, played: 0, wins: 0, losses: 0, win_rate: 0,
      elo: 1000, best_streak: 0, photo_path: photos[key] || null,
    });
  }, [board, photos]);

  const profileParamRef = useRef(false);
  useEffect(() => {
    if (!user || loading || profileParamRef.current) return;
    const p = new URLSearchParams(window.location.search).get("player");
    if (p) {
      profileParamRef.current = true;
      handleOpenProfile(p);
    }
  }, [user, loading, board, handleOpenProfile]);

  const ratingsParamRef = useRef(false);
  useEffect(() => {
    if (!user || ratingsParamRef.current || !matches.length) return;
    const q = new URLSearchParams(window.location.search);
    const m = q.get("ratings") && matches.find((x) => x.id === q.get("match"));
    if (m?.result) {
      ratingsParamRef.current = true;
      setRatingsMatch(m);
    }
  }, [user, matches]);


  const handleRecordResult = async (matchId, payload) => {
    try {
      await api.recordResult(matchId, payload);
      toast.success("Risultato salvato! 🏆 La partita è ora nella sezione Risultati");
      load();
      loadBoard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
      throw e;
    }
  };

  const handleToggleConfirm = async (match, confirm) => {
    try {
      if (confirm) {
        await api.confirmMatch(match.id, playerName);
        toast.success("Presenza confermata! ✅");
      } else {
        await api.unconfirmMatch(match.id, playerName);
        toast("Presenza annullata");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const handleUploadPhoto = async (name, file) => {
    toast.loading("Caricamento foto...", { id: "upload" });
    try {
      const res = await api.uploadPhoto(file);
      updateUser({ photo_path: res.photo_path });
      setHistoryPlayer((p) => (p && p.key === playerName.toLowerCase() ? { ...p, photo_path: res.photo_path } : p));
      toast.success("Foto aggiornata!", { id: "upload" });
      await loadPlayers();
      await loadBoard();
    } catch (e) {
      toast.error("Caricamento non riuscito", { id: "upload" });
    }
  };

  const openMatches = matches.filter((m) => !m.result);
  const resultMatches = matches
    .filter((m) => m.result && `${m.venue} ${m.date} ${m.players.map((p) => p.name).join(" ")}`.toLowerCase().includes(resultSearch.trim().toLowerCase()))
    .sort((a, b) => `${b.date} ${b.time}`.localeCompare(`${a.date} ${a.time}`));

  const filtered = openMatches.filter((m) => {
    const { confirmed } = confirmedAndWaitlist(m);
    const isFull = confirmed.length >= m.total_slots;
    const q = search.trim().toLowerCase();
    if (q && !(`${m.venue} ${m.date}`.toLowerCase().includes(q))) return false;
    if (filter === "disponibili") return !isFull;
    if (filter === "complete") return isFull;
    if (filter === "mie") return m.players.some((p) => p.name.toLowerCase() === playerName.toLowerCase());
    return true;
  });

  const todayMatches = openMatches.filter((m) => m.date === todayString());

  const knownNames = Array.from(new Set([
    ...board.map((r) => r.name),
    ...matches.flatMap((m) => m.players.map((p) => p.name)),
  ])).sort((a, b) => a.localeCompare(b));

  if (authLoading) {
    return <div className="min-h-screen bg-[#090d16] flex items-center justify-center text-slate-400">Caricamento...</div>;
  }
  if (!user) {
    return <AuthScreen />;
  }

  return (
    <div className="App min-h-screen bg-[#090d16] text-slate-100">
      <Toaster position="top-center" theme="dark" richColors />

      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#090d16]/85 backdrop-blur-xl border-b border-[#1e2c4a]">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-[#ccff00] flex items-center justify-center shrink-0">
              <Trophy className="w-5 h-5 text-[#090d16]" />
            </div>
            <div className="min-w-0">
              <h1 className="font-heading font-extrabold text-sm sm:text-lg leading-tight truncate">Organizza Incontri di Padel</h1>
              <p className="hidden sm:block text-[11px] text-slate-500 leading-none mt-0.5">Organizza · Condividi · Gioca</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
          {(installPrompt || (isIosDevice && !isStandalonePwa)) && (
            <button
              onClick={handleInstall}
              data-testid="install-app-btn"
              title="Installa app"
              className="w-10 h-10 rounded-full border bg-[#121b2d] border-[#1e2c4a] text-slate-400 hover:border-[#ccff00]/40 hover:text-[#ccff00] flex items-center justify-center transition-colors"
            >
              <Download className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={handleEnablePush}
            data-testid="enable-push-btn"
            title={pushOn ? "Notifiche attive" : "Attiva notifiche"}
            className={`w-10 h-10 rounded-full border flex items-center justify-center transition-colors ${
              pushOn ? "bg-[#ccff00]/15 border-[#ccff00]/40 text-[#ccff00]" : "bg-[#121b2d] border-[#1e2c4a] text-slate-400 hover:border-[#ccff00]/40"
            }`}
          >
            <Bell className="w-4 h-4" />
          </button>
          <button
            onClick={() => {
              const me = board.find((r) => r.key === playerName.toLowerCase())
                || { key: playerName.toLowerCase(), name: playerName, points: 0, played: 0, wins: 0, losses: 0, win_rate: 0, photo_path: user.photo_path };
              setHistoryPlayer(me);
            }}
            data-testid="player-badge"
            className="flex items-center gap-2 bg-[#121b2d] border border-[#1e2c4a] rounded-full pl-1.5 pr-3 py-1 hover:border-[#ccff00]/40 transition-colors"
          >
            <Avatar name={playerName} photoPath={user.photo_path} size={28} />
            <span className="text-sm font-semibold text-slate-200 max-w-[90px] truncate">{playerName}</span>
          </button>
          <button
            onClick={logout}
            data-testid="logout-btn"
            title="Esci"
            className="w-10 h-10 rounded-full border bg-[#121b2d] border-[#1e2c4a] text-slate-400 hover:border-[#ff4d4d]/40 hover:text-[#ff4d4d] flex items-center justify-center transition-colors"
          >
            <LogOut className="w-4 h-4" />
          </button>
          </div>
        </div>
      </header>

      {/* iOS install hint */}
      {showIosHint && (
        <div className="max-w-4xl mx-auto px-4 pt-3" data-testid="ios-install-banner">
          <div className="rounded-2xl bg-[#121b2d] border border-[#ccff00]/30 p-4 flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#ccff00] flex items-center justify-center shrink-0">
              <Download className="w-5 h-5 text-[#090d16]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading font-bold text-slate-100">Installa Padel all'improvviso</p>
              <p className="text-sm text-slate-400 mt-0.5 flex flex-wrap items-center gap-1">
                Tocca <Share className="inline w-4 h-4 text-[#ccff00]" /> <b className="text-slate-200">Condividi</b> in Safari, poi
                <b className="text-slate-200">“Aggiungi a schermata Home”</b> <Plus className="inline w-4 h-4 text-[#ccff00]" />.
              </p>
            </div>
            <button
              onClick={dismissIosHint}
              data-testid="ios-install-dismiss"
              className="text-slate-500 hover:text-slate-200 shrink-0"
              title="Chiudi"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="max-w-4xl mx-auto px-4 pt-5">
        <div className="flex gap-2 bg-[#121b2d] border border-[#1e2c4a] rounded-full p-1 w-full sm:w-auto sm:inline-flex">
          <TabBtn active={view === "partite"} onClick={() => setView("partite")} icon={<CalendarDays className="w-4 h-4" />} label="Partite" testid="tab-partite" />
          <TabBtn active={view === "risultati"} onClick={() => setView("risultati")} icon={<Trophy className="w-4 h-4" />} label="Risultati" testid="tab-risultati" />
          <TabBtn active={view === "classifica"} onClick={() => { setView("classifica"); loadBoard(); loadPlayers(); api.ratingsBoard().then(setRatingsRows).catch(() => {}); }} icon={<BarChart3 className="w-4 h-4" />} label="Classifica" testid="tab-classifica" />
          <TabBtn active={view === "votazioni"} onClick={() => setView("votazioni")} icon={<Vote className="w-4 h-4" />} label="Votazioni" testid="tab-votazioni" />
          {user?.role === "admin" && (
            <TabBtn active={view === "admin"} onClick={() => { setView("admin"); load(); }} icon={<ShieldCheck className="w-4 h-4" />} label="Admin" testid="tab-admin" />
          )}
        </div>
      </div>

      {view === "partite" ? (
      <>
      {/* Hero */}
      <section className="max-w-4xl mx-auto px-4 pt-5 pb-2">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4"
        >
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
              Prossime <span className="text-[#ccff00]">partite</span>
            </h2>
            <p className="text-slate-400 mt-1 text-sm">
              Crea una partita e condividila nel gruppo WhatsApp in un tap.
            </p>
          </div>
          <CreateMatchDialog
            playerName={playerName}
            onCreate={handleCreate}
            onRequireName={() => {}}
          />
        </motion.div>

        {/* Today reminder banner */}
        {todayMatches.length > 0 && (
          <div className="mt-5 rounded-2xl bg-[#ff9f1c]/10 border border-[#ff9f1c]/30 p-4" data-testid="today-reminder">
            <div className="flex items-center gap-2 text-[#ff9f1c] text-xs font-bold uppercase tracking-wider mb-2">
              <Bell className="w-4 h-4" /> Si gioca oggi · promemoria
            </div>
            <div className="space-y-2">
              {todayMatches.map((m) => {
                const { confirmed } = confirmedAndWaitlist(m);
                const missing = missingConfirmations(m);
                const confCount = confirmed.length - missing.length;
                const amConfirmedPlayer = confirmed.some((p) => p.name.toLowerCase() === playerName.toLowerCase());
                const iMissing = amConfirmedPlayer && missing.some((n) => n.toLowerCase() === playerName.toLowerCase());
                return (
                  <div key={m.id} className="bg-[#0b1420] rounded-xl px-3 py-2.5" data-testid={`today-match-${m.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-100 truncate">{m.venue}</p>
                        <p className="text-xs text-slate-400">⏰ {m.time} · <span className="text-[#25D366]">{confCount}/{confirmed.length} confermati</span></p>
                      </div>
                      {iMissing && (
                        <Button
                          onClick={() => handleToggleConfirm(m, true)}
                          data-testid={`today-confirm-btn-${m.id}`}
                          className="bg-[#ccff00] hover:bg-[#aeea00] text-[#090d16] font-bold rounded-full h-9 px-3 text-xs shrink-0"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Conferma
                        </Button>
                      )}
                    </div>
                    <div className="flex gap-2 mt-2">
                      {missing.length > 0 && (
                        <Button
                          onClick={() => openWhatsappConfirmReminder(m)}
                          data-testid={`solicit-confirm-btn-${m.id}`}
                          variant="outline"
                          className="flex-1 bg-transparent border-[#ff9f1c]/40 text-[#ff9f1c] hover:bg-[#ff9f1c]/10 hover:text-[#ff9f1c] font-semibold rounded-full h-9 px-3 text-xs"
                        >
                          <Bell className="w-3.5 h-3.5 mr-1" /> Sollecita conferme ({missing.length})
                        </Button>
                      )}
                      <Button
                        onClick={() => openWhatsappReminder(m)}
                        data-testid={`today-remind-btn-${m.id}`}
                        className="flex-1 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold rounded-full h-9 px-3 text-xs"
                      >
                        <MessageCircle className="w-3.5 h-3.5 mr-1" /> Invia al gruppo
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* View toggle */}
        <div className="flex gap-2 bg-[#121b2d] border border-[#1e2c4a] rounded-full p-1 w-full sm:w-auto sm:inline-flex mt-5">
          <button
            onClick={() => setPartiteView("lista")}
            data-testid="view-lista"
            className={`flex-1 sm:flex-none px-5 py-2 rounded-full text-sm font-semibold transition-colors ${partiteView === "lista" ? "bg-[#ccff00] text-[#090d16]" : "text-slate-300 hover:text-white"}`}
          >
            Lista
          </button>
          <button
            onClick={() => setPartiteView("calendario")}
            data-testid="view-calendario"
            className={`flex-1 sm:flex-none px-5 py-2 rounded-full text-sm font-semibold transition-colors ${partiteView === "calendario" ? "bg-[#ccff00] text-[#090d16]" : "text-slate-300 hover:text-white"}`}
          >
            Calendario
          </button>
        </div>

        {partiteView === "lista" && (
        <>
        {/* Search */}
        <div className="relative mt-4">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per circolo o data (es. Milano o 2026-06-21)"
            data-testid="search-input"
            className="w-full bg-[#121b2d] border border-[#1e2c4a] rounded-full h-11 pl-10 pr-10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-[#ccff00]/50"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              data-testid="clear-search"
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200 text-lg leading-none"
            >
              ×
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex gap-2 mt-4 overflow-x-auto pb-1 -mx-1 px-1">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`filter-${f.id}`}
              className={`shrink-0 text-sm font-semibold rounded-full px-4 py-2 transition-colors ${
                filter === f.id
                  ? "bg-[#ccff00] text-[#090d16]"
                  : "bg-[#121b2d] text-slate-300 border border-[#1e2c4a] hover:border-[#ccff00]/40"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        </>
        )}
      </section>

      {/* List / Calendar */}
      <main className="max-w-4xl mx-auto px-4 pb-24 pt-4">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[0, 1].map((i) => (
              <div key={i} className="h-72 rounded-3xl bg-[#121b2d] border border-[#1e2c4a] animate-pulse" />
            ))}
          </div>
        ) : partiteView === "calendario" ? (
          <div className="space-y-4">
            <MonthCalendar matches={openMatches} selected={selectedDate} onSelect={setSelectedDate} />
            {selectedDate ? (
              matchesOnDate(openMatches, selectedDate).length ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {matchesOnDate(openMatches, selectedDate).map((m, i) => (
                    <MatchCard key={m.id} match={m} index={i} playerName={playerName} photos={photos}
                      onJoin={handleJoin} onLeave={handleLeave} onShare={setShareMatch}
                      onDelete={handleDelete} onRecordResult={setResultMatch} onToggleConfirm={handleToggleConfirm} onOpenComments={setCommentsMatch}
                      onKick={handleKick} onPromote={handlePromote} onEdit={setEditMatch} onOpenProfile={handleOpenProfile} onOpenRatings={setRatingsMatch} />
                  ))}
                </div>
              ) : null
            ) : (
              <p className="text-center text-slate-500 text-sm py-4">Tocca un giorno evidenziato per vedere le partite.</p>
            )}
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState filter={filter} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map((m, i) => (
              <MatchCard
                key={m.id}
                match={m}
                index={i}
                playerName={playerName}
                photos={photos}
                onJoin={handleJoin}
                onLeave={handleLeave}
                onShare={setShareMatch}
                onDelete={handleDelete}
                onRecordResult={setResultMatch}
                onToggleConfirm={handleToggleConfirm}
                onOpenComments={setCommentsMatch}
                onOpenRatings={setRatingsMatch}
                onKick={handleKick}
                onPromote={handlePromote}
                onEdit={setEditMatch}
                onOpenProfile={handleOpenProfile}
              />
            ))}
          </div>
        )}
      </main>
      </>
      ) : view === "risultati" ? (
      <main className="max-w-4xl mx-auto px-4 pb-24 pt-5" data-testid="results-view">
        <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
          <span className="text-[#ccff00]">Risultati</span>
        </h2>
        <p className="text-slate-400 mt-1 mb-4 text-sm">Tutte le partite giocate, dalla più recente. Da qui dai i voti e condividi il tabellone.</p>
        <div className="relative mb-5">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={resultSearch} onChange={(e) => setResultSearch(e.target.value)} placeholder="Cerca per circolo, data o giocatore..."
            data-testid="results-search" className="pl-9 bg-[#121b2d] border-[#1e2c4a] text-slate-100 h-11 rounded-xl" />
        </div>
        {resultMatches.length === 0 ? (
          <p className="text-center text-slate-500 text-sm py-12" data-testid="results-empty">Nessun risultato ancora. Quando registri il risultato di una partita la trovi qui.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {resultMatches.map((m, i) => (
              <MatchCard key={m.id} match={m} index={i} playerName={playerName} photos={photos}
                onJoin={handleJoin} onLeave={handleLeave} onShare={setShareMatch}
                onDelete={handleDelete} onRecordResult={setResultMatch} onToggleConfirm={handleToggleConfirm} onOpenComments={setCommentsMatch}
                onKick={handleKick} onPromote={handlePromote} onEdit={setEditMatch} onOpenProfile={handleOpenProfile} onOpenRatings={setRatingsMatch} />
            ))}
          </div>
        )}
      </main>
      ) : view === "admin" && user?.role === "admin" ? (
      <main className="max-w-4xl mx-auto px-4 pb-24 pt-5">
        <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">
          <span className="text-[#ccff00]">Pannello</span> amministratore
        </h2>
        <p className="text-slate-400 mb-5 text-sm">Gestisci iscritti, partite, votazioni e pagelle. Su partite e chat hai anche i pulsanti di modifica ed eliminazione.</p>
        <AdminPanel matches={matches} onMatchesChanged={() => { load(); loadBoard(); }} />
      </main>
      ) : view === "votazioni" ? (
      <main className="max-w-4xl mx-auto px-4 pb-24 pt-5">
        <div className="mb-5">
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
            <span className="text-[#ccff00]">Votazioni</span> dell'anno
          </h2>
          <p className="text-slate-400 mt-1 text-sm">
            Vota il migliore di destra e di sinistra, l'Avvocato, il più leale, il più disonesto e il professore. Puoi cambiare idea fino al 31/12.
          </p>
        </div>
        <VotesBoard playerName={playerName} />
      </main>
      ) : (
      <main className="max-w-4xl mx-auto px-4 pb-24 pt-5">
        <div className="mb-5 flex items-end justify-between gap-3">
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
              <span className="text-[#ccff00]">Classifica</span>
            </h2>
            <p className="text-slate-400 mt-1 text-sm">
              Vittoria = 3 punti · Sconfitta = 1 punto · 🔥 serie positiva.
            </p>
          </div>
          {board.length > 0 && (
            <Button
              onClick={() => openWhatsappLeaderboard(board)}
              data-testid="share-leaderboard-btn"
              className="bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold rounded-full h-11 px-4 shrink-0"
            >
              <Share2 className="w-4 h-4 mr-1.5" /> Condividi
            </Button>
          )}
        </div>

        <div className="flex gap-2 bg-[#121b2d] border border-[#1e2c4a] rounded-full p-1 w-full sm:w-auto sm:inline-flex mb-5">
          <button
            onClick={() => setBoardView("giocatori")}
            data-testid="board-view-giocatori"
            className={`flex-1 sm:flex-none px-5 py-2 rounded-full text-sm font-semibold transition-colors ${boardView === "giocatori" ? "bg-[#ccff00] text-[#090d16]" : "text-slate-300 hover:text-white"}`}
          >
            Giocatori
          </button>
          <button
            onClick={() => setBoardView("coppie")}
            data-testid="board-view-coppie"
            className={`flex-1 sm:flex-none px-5 py-2 rounded-full text-sm font-semibold transition-colors ${boardView === "coppie" ? "bg-[#ccff00] text-[#090d16]" : "text-slate-300 hover:text-white"}`}
          >
            Coppie
          </button>
          <button
            onClick={() => { setBoardView("pagelle"); api.ratingsBoard().then(setRatingsRows).catch(() => toast.error("Impossibile caricare le pagelle")); }}
            data-testid="board-view-pagelle"
            className={`flex-1 sm:flex-none px-5 py-2 rounded-full text-sm font-semibold transition-colors ${boardView === "pagelle" ? "bg-[#ccff00] text-[#090d16]" : "text-slate-300 hover:text-white"}`}
          >
            Pagelle
          </button>
        </div>

        {boardView === "giocatori" ? (
          <Leaderboard rows={board} mvp={mvp} pastMvps={pastMvps} currentName={playerName} onUploadPhoto={handleUploadPhoto} onOpenHistory={setHistoryPlayer} />
        ) : boardView === "pagelle" ? (
          <RatingsBoard rows={ratingsRows} currentName={playerName} onOpenHistory={(r) => handleOpenProfile(r.name)} />
        ) : (
          <PairsBoard pairs={pairs} />
        )}
      </main>
      )}

      <AwardsCeremony username={playerName} />
      <ShareDialog match={shareMatch} onClose={() => setShareMatch(null)} />
      <ResultDialog match={resultMatch} onClose={() => setResultMatch(null)} onSave={handleRecordResult} />
      <RatingsDialog match={ratingsMatch} playerName={playerName} photos={photos} onClose={() => { setRatingsMatch(null); api.ratingsBoard().then(setRatingsRows).catch(() => {}); }} />
      <EditMatchDialog match={editMatch} onClose={() => setEditMatch(null)} onSave={handleEditMatch} />
      <PlayerHistoryDialog player={historyPlayer} allPlayers={board} currentName={playerName} onUploadPhoto={handleUploadPhoto} onClose={() => setHistoryPlayer(null)}
        onAdminChanged={() => { load(); loadBoard(); loadPlayers(); }} />
      <CommentsDialog
        match={commentsMatch}
        playerName={playerName}
        names={knownNames}
        onClose={() => setCommentsMatch(null)}
        onAdd={handleAddComment}
        onDelete={handleDeleteComment}
        onReact={handleReactComment}
        onReply={handleReply}
        onDeleteReply={handleDeleteReply}
        onRequireName={() => {}}
      />
    </div>
  );
}

const TabBtn = ({ active, onClick, icon, label, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
      active ? "bg-[#ccff00] text-[#090d16]" : "text-slate-300 hover:text-white"
    }`}
  >
    {icon} <span className={active ? "" : "hidden sm:inline"}>{label}</span>
  </button>
);

const EmptyState = ({ filter }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    data-testid="empty-state"
    className="flex flex-col items-center justify-center text-center py-20"
  >
    <div className="w-16 h-16 rounded-2xl bg-[#121b2d] border border-[#1e2c4a] flex items-center justify-center mb-4">
      <CalendarX2 className="w-8 h-8 text-slate-500" />
    </div>
    <h3 className="font-heading font-bold text-xl text-slate-200">
      {filter === "mie" ? "Non sei iscritto a nessuna partita" : "Nessuna partita in programma"}
    </h3>
    <p className="text-slate-500 text-sm mt-1 max-w-xs">
      Crea la prima partita e invita gli amici dal gruppo WhatsApp.
    </p>
  </motion.div>
);

const ShareDialog = ({ match, onClose }) => {
  const [mode, setMode] = useState("invito");
  useEffect(() => { if (match) setMode("invito"); }, [match]);
  if (!match) return null;
  const text = mode === "invito" ? buildWhatsappText(match) : buildReminderText(match);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Testo copiato!");
    } catch {
      toast.error("Copia non riuscita");
    }
  };
  const openWa = () => { (mode === "invito" ? openWhatsapp : openWhatsappReminder)(match); onClose(); };
  return (
    <Dialog open={!!match} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#121b2d] border-[#1e2c4a] text-slate-100 max-w-md" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <Share2 className="w-5 h-5 text-[#25D366]" /> Condividi su WhatsApp
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Scegli invito o promemoria, poi copia o apri WhatsApp.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 bg-[#0b1420] border border-[#1e2c4a] rounded-full p-1">
          <button
            onClick={() => setMode("invito")}
            data-testid="share-mode-invito"
            className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${mode === "invito" ? "bg-[#ccff00] text-[#090d16]" : "text-slate-300"}`}
          >
            📣 Invito
          </button>
          <button
            onClick={() => setMode("promemoria")}
            data-testid="share-mode-promemoria"
            className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${mode === "promemoria" ? "bg-[#ff9f1c] text-[#090d16]" : "text-slate-300"}`}
          >
            ⏰ Promemoria
          </button>
        </div>
        <div className="rounded-2xl bg-[#0b1420] border border-[#1e2c4a] p-4 max-h-64 overflow-y-auto">
          <pre className="whitespace-pre-wrap font-sans text-sm text-slate-200 leading-relaxed">{text}</pre>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={copy}
            variant="outline"
            data-testid="copy-text-btn"
            className="flex-1 bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white rounded-xl h-11"
          >
            <Copy className="w-4 h-4 mr-1.5" /> Copia
          </Button>
          <Button
            onClick={openWa}
            data-testid="open-whatsapp-btn"
            className="flex-1 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold rounded-xl h-11"
          >
            <MessageCircle className="w-4 h-4 mr-1.5" /> Apri WhatsApp
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default App;
